import { Tooltip } from '@nextui-org/react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CHIP_GAP = 4;

const getGroupBadgeClass = (group: string): string => {
  const key = group.toLowerCase();

  if (key.includes('admin')) {
    return 'bg-danger/10 text-danger';
  }
  if (key.includes('manager') || key.includes('editor')) {
    return 'bg-primary/10 text-primary';
  }
  if (key.includes('viewer') || key.includes('user')) {
    return 'bg-default-100 text-default-700';
  }
  if (key.includes('analyst')) {
    return 'bg-purple-100 text-purple-800';
  }

  return 'bg-default-100 text-default-600';
};

const chipClassName = (group: string) =>
  `inline-flex max-w-full shrink-0 items-center truncate rounded-full px-2 py-0.5 text-xs font-medium ${getGroupBadgeClass(group)}`;

interface UserRolesBadgesProps {
  groups: string[];
  className?: string;
}

const UserRolesBadges = ({ groups, className = '' }: UserRolesBadgesProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(groups.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;

    if (!container || !measure || groups.length === 0) {
      setVisibleCount(0);
      return;
    }

    const calculateVisibleCount = () => {
      const availableWidth = container.clientWidth;
      if (availableWidth <= 0) {
        return;
      }

      const chipElements = Array.from(
        measure.querySelectorAll('[data-role-chip]'),
      ) as HTMLElement[];
      const overflowElement = measure.querySelector(
        '[data-overflow-chip]',
      ) as HTMLElement | null;
      const overflowWidth = overflowElement?.offsetWidth ?? 32;

      if (groups.length === 1) {
        setVisibleCount(1);
        return;
      }

      let usedWidth = 0;
      let count = 0;

      for (let index = 0; index < chipElements.length; index += 1) {
        const chipWidth = chipElements[index].offsetWidth;
        const hiddenCount = chipElements.length - (index + 1);
        const overflowReserve =
          hiddenCount > 0 ? overflowWidth + CHIP_GAP : 0;
        const gapBeforeChip = count > 0 ? CHIP_GAP : 0;
        const totalWidth = usedWidth + gapBeforeChip + chipWidth + overflowReserve;

        if (totalWidth > availableWidth) {
          break;
        }

        usedWidth += gapBeforeChip + chipWidth;
        count = index + 1;
      }

      setVisibleCount(Math.max(1, count || 1));
    };

    calculateVisibleCount();

    const observer = new ResizeObserver(calculateVisibleCount);
    observer.observe(container);

    return () => observer.disconnect();
  }, [groups]);

  if (!groups.length) {
    return null;
  }

  const visibleGroups = groups.slice(0, visibleCount);
  const hiddenGroups = groups.slice(visibleCount);
  const hiddenRoleLabels = hiddenGroups.join(', ');

  return (
    <div className={`relative min-w-0 ${className}`}>
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute h-0 overflow-hidden whitespace-nowrap"
      >
        {groups.map((group) => (
          <span key={group} data-role-chip className={chipClassName(group)}>
            {group}
          </span>
        ))}
        <span
          data-overflow-chip
          className="inline-flex items-center rounded-full bg-default-100 text-xs font-medium text-default-600"
        >
          +{groups.length}
        </span>
      </div>
      <div
        ref={containerRef}
        className="flex min-w-0 flex-nowrap items-center gap-1 overflow-hidden"
      >
        {visibleGroups.map((group) => (
          <span key={group} className={chipClassName(group)} title={group}>
            {group}
          </span>
        ))}
        {hiddenGroups.length > 0 && (
          <Tooltip
            content={hiddenRoleLabels}
            color="default"
            showArrow
            classNames={{
              content: 'max-w-xs bg-[#101828] text-sm text-white',
            }}
          >
            <span
              aria-label={t('Additional roles: {{roles}}', {
                roles: hiddenRoleLabels,
              })}
              className="inline-flex shrink-0 cursor-default items-center rounded-full bg-default-100 text-xs font-medium text-default-600"
            >
              +{hiddenGroups.length}
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export { UserRolesBadges };
